'use client';

import { use } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import Link from 'next/link';
import { Book, Code, FileText, Zap, ArrowRight, Sparkles } from 'lucide-react';

export default function DocumentationPage({ params }: { params: Promise<{ locale: string }> }) {
  const { locale } = use(params);

  const docSections = [
    {
      icon: Book,
      title: 'Getting Started',
      description: 'Quick start guide to set up your first backup in minutes',
      href: `/${locale}/resources/getting-started`,
      gradient: 'from-blue-600 to-cyan-600',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: Code,
      title: 'API Reference',
      description: 'Complete API documentation for automation and integration',
      href: `/${locale}/resources/api`,
      gradient: 'from-purple-600 to-pink-600',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      icon: FileText,
      title: 'Guides & Tutorials',
      description: 'Step-by-step tutorials for common backup scenarios',
      href: `/${locale}/resources/guides`,
      gradient: 'from-green-600 to-emerald-600',
      iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      icon: Zap,
      title: 'Best Practices',
      description: 'Production-ready patterns and recommendations',
      href: `/${locale}/resources/best-practices`,
      gradient: 'from-orange-600 to-red-600',
      iconColor: 'text-orange-600 dark:text-orange-400',
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />

      <div className="relative container mx-auto px-4 py-16 pt-32 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-sm shadow-lg shadow-blue-500/50 mb-6">
            <Sparkles className="w-4 h-4" />
            Documentation Hub
          </div>
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight mb-6">
            Documentation
          </h1>
          <p className="text-2xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Comprehensive guides and API references for Next PAL Nexus
          </p>
        </div>

        {/* Documentation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {docSections.map((section) => (
            <Link
              key={section.title}
              href={section.href}
              className="group relative"
            >
              <div className={`absolute -inset-1 bg-gradient-to-r ${section.gradient} rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-500`} />
              <div className="relative h-full p-8 rounded-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-white/20 dark:border-slate-700/50  transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg`}>
                    <section.icon className="h-7 w-7 text-white" />
                  </div>
                  <ArrowRight className="h-6 w-6 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {section.title}
                </h2>
                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                  {section.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Links Section */}
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-pink-600 rounded-xl blur opacity-20" />
          <div className="relative p-8 rounded-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-white/20 dark:border-slate-700/50">
            <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-6">Quick Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                href={`/${locale}/resources/blog`}
                className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:text-white transition-all"
              >
                <span className="font-semibold">Blog</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href={`/${locale}/resources/guides`}
                className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:text-white transition-all"
              >
                <span className="font-semibold">Guides</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/contact"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:text-white transition-all"
              >
                <span className="font-semibold">Contact Support</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
